/**
 * React Query hooks for Groups management
 * 
 * Provides data fetching and mutations for group management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface Group {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  type: 'system' | 'custom';
  created_at: string;
  updated_at: string;
  member_count: number;
}

export interface GroupMember {
  user_id: string;
  email: string;
  name: string;
  role: string;
  added_at: string;
}

export interface GroupListResponse {
  items: Group[];
  total: number;
  page: number;
  page_size: number;
}

export interface GroupMemberListResponse {
  items: GroupMember[];
  total: number;
}

export interface GroupCreate {
  name: string;
  description?: string;
  type?: 'system' | 'custom';
  tenant_id: string;
}

export interface GroupUpdate {
  name?: string;
  description?: string;
}

export interface GroupMemberAdd {
  user_id: string;
  role?: string;
}

// =============================================================================
// Query Keys
// =============================================================================

const GROUPS_KEY = 'groups';

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch all groups
 */
export function useGroups(options?: { type?: string; page?: number; page_size?: number }) {
  return useQuery({
    queryKey: [GROUPS_KEY, options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.type) params.set('type', options.type);
      if (options?.page) params.set('page', options.page.toString());
      if (options?.page_size) params.set('page_size', options.page_size.toString());
      
      const queryString = params.toString();
      const url = queryString ? `/api/v1/groups?${queryString}` : '/api/v1/groups';
      const { data } = await apiClient.get<GroupListResponse>(url);
      return data;
    },
  });
}

/**
 * Fetch single group by ID
 */
export function useGroup(id: string) {
  return useQuery({
    queryKey: [GROUPS_KEY, id],
    queryFn: async () => {
      const { data } = await apiClient.get<Group>(`/api/v1/groups/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

/**
 * Fetch group members
 */
export function useGroupMembers(groupId: string) {
  return useQuery({
    queryKey: [GROUPS_KEY, groupId, 'members'],
    queryFn: async () => {
      const { data } = await apiClient.get<GroupMemberListResponse>(`/api/v1/groups/${groupId}/members`);
      return data;
    },
    enabled: !!groupId,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Create new group
 */
export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GroupCreate) => {
      const { data } = await apiClient.post<Group>('/api/v1/groups', input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] });
    },
  });
}

/**
 * Update group
 */
export function useUpdateGroup(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GroupUpdate) => {
      const { data } = await apiClient.put<Group>(`/api/v1/groups/${id}`, input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, id] });
    },
  });
}

/**
 * Delete group
 */
export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/groups/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY] });
    },
  });
}

/**
 * Add member to group
 */
export function useAddGroupMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GroupMemberAdd) => {
      const { data } = await apiClient.post<GroupMember>(`/api/v1/groups/${groupId}/members`, input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, groupId, 'members'] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, groupId] });
    },
  });
}

/**
 * Remove member from group
 */
export function useRemoveGroupMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/api/v1/groups/${groupId}/members/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, groupId, 'members'] });
      queryClient.invalidateQueries({ queryKey: [GROUPS_KEY, groupId] });
    },
  });
}
